<script lang="ts" setup>
import {useQuasar} from 'quasar';
import {useRouter} from 'vue-router';
import {useSessionStore} from 'src/store';
import {useI18n} from 'vue-i18n';
import {
  computed,
  onMounted,
  ref
} from 'vue';
import {handleApiRequest} from 'src/api/handleApiRequest';
import {
  getPatientProcedures,
  setSelectedProcedure
} from 'src/api/endpoints/userWeb';
import {factorProcedures} from 'src/utils/procedureUtils';
import axios from 'axios';
import {userLogout} from '@/services/userService';
import Box from 'src/components/BoxContainer.vue';
import EocNavBtn from 'src/components/EocNavBtn.vue';

const $q = useQuasar(),
  router = useRouter(),
  sessionStore = useSessionStore(),
  {t} = useI18n();

// Component options
const hasDisabledProcedure = ref(false),
  showDisabledProcedure = ref(false);

// Computed properties
const firstName = computed(() => sessionStore.user?.firstName),

  navigatableProcedures = ref( []),

  sessionProcedures = computed(() => sessionStore.procedures || []),

  medstratLinkPatientId = computed(() => sessionStore.user.medstratPatientId);


// Methods
const onViewAllClicked = () => {
  showDisabledProcedure.value = !showDisabledProcedure.value;
};

const isMagicLinkUser = computed(() => sessionStore.isMagicLinkUser);

onMounted(async () => {
  //Force redirect to change password
  if (sessionStore.requiresPasswordChange)
    router.push({ name: 'Settings.ChangePassword', params: { forcePasswordChange: 'true' } });

  document.querySelectorAll('html')[0].scrollTop = 0;

  sessionStore.setIsHeaderVisible(false);
  sessionStore.setIsFooterVisible(false);

  $q.loading.show();

  // Get Patient Procedures
  const getPatientProcRsp = await handleApiRequest(() => getPatientProcedures(), null, $q);

  if (getPatientProcRsp?.result) {
    let episodesOfCare = getPatientProcRsp.result.episodesOfCare;

    //overrideLength check for medstrat since it has a fake procedure and length SHOULd be > 1
    //for what is shown on the select care plan page
    let overrideLengthCheck =  medstratLinkPatientId.value ? true : false;
    factorProcedures(episodesOfCare, overrideLengthCheck, isMagicLinkUser.value);
    //we return the final list of procedures instead of something more traditional like a computed
    //because the event dispatch to the store isn't guaranteed to finish before continuing onwards
    //in this section of code at which point we wouldn't have a completely accurate list
    const loadedSessionProcedures = sessionStore.loadProcedures(episodesOfCare);
    navigatableProcedures.value = sessionStore.navigatableProcedures(loadedSessionProcedures);


    hasDisabledProcedure.value = sessionProcedures.value.find((x) => x.status === 3) !== undefined;

    const redirectProcedure = sessionStore.magicLinkEocId ?? (navigatableProcedures.value.length === 1 //shell eoc scenario
      ? navigatableProcedures.value[0]?.eocId
      : null);

    if (redirectProcedure) {
      const id = redirectProcedure;

      try {
        const selectedProcedure = await setSelectedProcedure(id);

        if (selectedProcedure) {
          sessionStore.setSelectedEocId(id);
          await router.push({ name: 'Interrupts' });
          return;
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          $q.notify({ type: 'error', message: t(error.response?.data?.errorMessageKey).toString() })
        }
      }
    }

    sessionStore.setIsHeaderVisible(true);
    sessionStore.setIsFooterVisible(true);
    $q.loading.hide();
  } else {
    $q.loading.hide();
    await userLogout($q);
  }
});
</script>

<template>
  <q-page class="not-auth-header select-care-plan" padding>
    <div v-if="navigatableProcedures.length > 1" class="row bs-gutter justify-center">

      <!-- Box -->
      <Box :box-bkgd-color="$zb.enums.BoxBackgroundColorEnum.linearGradient"
        :box-brdr-radius="$zb.enums.BoxBorderRadiusEnum.lg" :box-drop-shadow="$zb.enums.BoxDropShadowEnum.xl"
        :box-padding="$zb.enums.BoxPaddingEnum.p505065" :box-width="$zb.enums.BoxWidthEnum.md" class="col-auto">

        <!-- Heading -->
        <div class="row heading">
          <div class="col text-center mb-50">
            <h1 class="text-content-grd-bg-content q-mb-lg">
              {{ t('vue3_eoc_selection_header', [firstName]) }}
            </h1>
            <p class="text-content-grd-bg-content font-size-20">
              {{ t('eoc_selection_subheader') }}
            </p>
          </div>
        </div>

        <!-- Procedures -->
        <div class="procedures">
          <div class="column">
            <EocNavBtn v-for="procedure in navigatableProcedures" :key="procedure.eocId" :procedure="procedure"
              :showDisabledProcs="showDisabledProcedure" />
          </div>
        </div>

        <!-- View All -->
        <div v-if="hasDisabledProcedure" v-show="!showDisabledProcedure" class="row justify-center pt-30">
          <div class="col-auto">
            <zbm-btn-link @click="onViewAllClicked" class="font-size-14" color="content-grd-bg-content"
              icon-right="fa-solid fa-angle-down" :label="t('eoc_view_all')" :no-caps="false" />
          </div>
        </div>
      </Box>
    </div>
  </q-page>
</template>

<style scoped lang="scss">
.select-care-plan {

  // Procedures
  .procedures { // .row
    margin-inline: -50px;
  }
}
</style>
